import { type CollisionBox, type CollisionIndex } from "./collision";

export interface ShapeTextPlacement {
	direction: "center" | "left" | "right" | "up" | "down";
	dx: number;
	dy: number;
}

export interface ShapeTextLayoutResult {
	startX: number;
	startY: number;
	textX: number;
	textAlign: CanvasTextAlign;
	placedCenter: boolean;
	placementIndex: number;
}

interface ShapeTextLayoutInput {
	sx: number;
	sy: number;
	blockWidth: number;
	blockHeight: number;
	textRotation: number;
	placements: ShapeTextPlacement[];
	markerId: string;
	tree: CollisionIndex;
	cachedIndex?: number;
	/** When true, reuse the cached placement without collision checks or inserting into the tree. */
	skipCollision?: boolean;
}

export function getShapeTextPlacements(textAnchor: "center" | "center-fixed" | "left" | "right" | "top" | "bottom"): ShapeTextPlacement[] {
	const offset = 10;
	const centered = { direction: "center", dx: 0, dy: 0 } as const;
	const right = { direction: "right", dx: offset, dy: 0 } as const;
	const left = { direction: "left", dx: -offset, dy: 0 } as const;
	const up = { direction: "up", dx: 0, dy: -offset } as const;
	const down = { direction: "down", dx: 0, dy: offset } as const;

	if (textAnchor === "left") return [right];
	if (textAnchor === "right") return [left];
	if (textAnchor === "top") return [down];
	if (textAnchor === "bottom") return [up];
	if (textAnchor === "center-fixed") return [centered];
	return [centered, right, left, up, down];
}

function buildShapeTextLayout(input: ShapeTextLayoutInput, placementIndex: number) {
	const { direction, dx, dy } = input.placements[placementIndex];
	let startX: number;
	let startY: number;
	let textAlign: CanvasTextAlign;

	if (direction === "center") {
		startX = input.sx + dx - input.blockWidth / 2;
		startY = input.sy + dy - input.blockHeight / 2;
		textAlign = "center";
	} else if (direction === "left") {
		startX = input.sx + dx - input.blockWidth;
		startY = input.sy + dy - input.blockHeight / 2;
		textAlign = "right";
	} else if (direction === "up") {
		startX = input.sx + dx - input.blockWidth / 2;
		startY = input.sy + dy - input.blockHeight;
		textAlign = "center";
	} else if (direction === "down") {
		startX = input.sx + dx - input.blockWidth / 2;
		startY = input.sy + dy;
		textAlign = "center";
	} else {
		startX = input.sx + dx;
		startY = input.sy + dy - input.blockHeight / 2;
		textAlign = "left";
	}

	return {
		startX,
		startY,
		textX: input.sx + dx,
		textAlign,
		placedCenter: direction === "center",
		placementIndex,
	} satisfies ShapeTextLayoutResult;
}

export function resolveShapeTextLayout(input: ShapeTextLayoutInput): ShapeTextLayoutResult | undefined {
	if (input.skipCollision) {
		const placementIndex = Math.min(input.cachedIndex ?? 0, input.placements.length - 1);
		return buildShapeTextLayout(input, placementIndex);
	}

	const order = input.cachedIndex != null && input.cachedIndex < input.placements.length
		? [input.cachedIndex, ...input.placements.map((_, index) => index).filter((index) => index !== input.cachedIndex)]
		: input.placements.map((_, index) => index);

	for (const placementIndex of order) {
		const layout = buildShapeTextLayout(input, placementIndex);

		const inserted = input.textRotation
			? insertRotatedCollisionTiles(input.tree, {
				sx: input.sx,
				sy: input.sy,
				startX: layout.startX,
				startY: layout.startY,
				blockWidth: input.blockWidth,
				blockHeight: input.blockHeight,
				textRotation: input.textRotation,
				markerId: input.markerId,
			})
			: insertPlainCollisionBox(input.tree, {
				startX: layout.startX,
				startY: layout.startY,
				blockWidth: input.blockWidth,
				blockHeight: input.blockHeight,
				markerId: input.markerId,
			});

		if (!inserted) continue;

		return layout;
	}
}

function insertPlainCollisionBox(
	tree: CollisionIndex,
	input: { startX: number; startY: number; blockWidth: number; blockHeight: number; markerId: string },
) {
	const labelBox: CollisionBox = {
		minX: input.startX,
		minY: input.startY,
		maxX: input.startX + input.blockWidth,
		maxY: input.startY + input.blockHeight,
		markerId: input.markerId,
		isLabel: true,
	};
	if (tree.collides(labelBox)) return false;
	tree.insert(labelBox);
	return true;
}

function insertRotatedCollisionTiles(
	tree: CollisionIndex,
	input: {
		sx: number;
		sy: number;
		startX: number;
		startY: number;
		blockWidth: number;
		blockHeight: number;
		textRotation: number;
		markerId: string;
	},
) {
	const rad = (input.textRotation * Math.PI) / 180;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	const rotatePoint = (px: number, py: number) => {
		const rdx = px - input.sx;
		const rdy = py - input.sy;
		return [input.sx + rdx * cos - rdy * sin, input.sy + rdx * sin + rdy * cos] as const;
	};

	const tileSize = Math.max(input.blockHeight * 0.9, 7);
	const insetX = Math.min(tileSize * 0.2, 2);
	const insetY = Math.min(input.blockHeight * 0.22, 2.5);
	const tileCount = Math.max(1, Math.ceil(input.blockWidth / tileSize));
	const tileBoxes: CollisionBox[] = [];

	for (let tileIndex = 0; tileIndex < tileCount; tileIndex++) {
		const tileStartX = input.startX + tileIndex * tileSize;
		const tileEndX = Math.min(input.startX + input.blockWidth, tileStartX + tileSize);
		let innerStartX = tileStartX + insetX;
		let innerEndX = tileEndX - insetX;
		if (innerEndX <= innerStartX) {
			innerStartX = tileStartX;
			innerEndX = tileEndX;
		}

		let topY = input.startY + insetY;
		let bottomY = input.startY + input.blockHeight - insetY;
		if (bottomY <= topY) {
			topY = input.startY;
			bottomY = input.startY + input.blockHeight;
		}

		const p1 = rotatePoint(innerStartX, topY);
		const p2 = rotatePoint(innerEndX, topY);
		const p3 = rotatePoint(innerEndX, bottomY);
		const p4 = rotatePoint(innerStartX, bottomY);

		const tileBox: CollisionBox = {
			minX: Math.min(p1[0], p2[0], p3[0], p4[0]),
			minY: Math.min(p1[1], p2[1], p3[1], p4[1]),
			maxX: Math.max(p1[0], p2[0], p3[0], p4[0]),
			maxY: Math.max(p1[1], p2[1], p3[1], p4[1]),
			markerId: input.markerId,
			isLabel: true,
		};

		if (tree.collides(tileBox)) return false;
		tileBoxes.push(tileBox);
	}

	for (const tileBox of tileBoxes) {
		tree.insert(tileBox);
	}
	return true;
}